/**
 * Mobile App Detection and Communication Hook
 *
 * Detects when the app is running inside the React Native WebView
 * and provides utilities for communicating with the native app.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

// Extend Window interface for React Native WebView
declare global {
  interface Window {
    ReactNativeWebView?: {
      postMessage: (message: string) => void;
    };
  }
}

interface MobileAppMessage {
  type: "logout" | "navigate" | "console" | string;
  [key: string]: unknown;
}

/**
 * Check if running inside React Native WebView
 */
export const isInWebView = (): boolean => {
  return typeof window !== "undefined" && !!window.ReactNativeWebView;
};

/**
 * Check if running on a mobile device (touch-based)
 */
export const isMobileDevice = (): boolean => {
  if (typeof window === "undefined") return false;
  return (
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-expect-error - msMaxTouchPoints is IE-specific
    navigator.msMaxTouchPoints > 0
  );
};

/**
 * Hook for mobile app detection and communication
 */
export const useMobileApp = () => {
  const [isWebView, setIsWebView] = useState(false);

  useEffect(() => {
    setIsWebView(isInWebView());
  }, []);

  /**
   * Send a message to the React Native app
   */
  const postMessage = useCallback((message: MobileAppMessage) => {
    if (window.ReactNativeWebView) {
      try {
        window.ReactNativeWebView.postMessage(JSON.stringify(message));
      } catch (error) {
        console.error("[MobileApp] Failed to post message:", error);
      }
    }
  }, []);

  /**
   * Request logout from native app (clears secure storage)
   */
  const requestLogout = useCallback(() => {
    postMessage({ type: "logout" });
  }, [postMessage]);

  /**
   * Request navigation to a native screen
   */
  const requestNavigate = useCallback(
    (path: string) => {
      postMessage({ type: "navigate", path });
    },
    [postMessage],
  );

  /**
   * Log to native app console (useful for debugging)
   */
  const logToNative = useCallback(
    (level: "info" | "warn" | "error", message: string) => {
      postMessage({ type: "console", level, message });
    },
    [postMessage],
  );

  return useMemo(
    () => ({
      isWebView,
      isMobile: isMobileDevice(),
      postMessage,
      requestLogout,
      requestNavigate,
      logToNative,
    }),
    [isWebView, postMessage, requestLogout, requestNavigate, logToNative],
  );
};

/**
 * Hook that returns true if the app should show a minimal UI
 * (when embedded in mobile app WebView)
 */
export const useMinimalUI = (): boolean => {
  const { isWebView } = useMobileApp();
  return isWebView;
};

export default useMobileApp;
